// Content sections component
import { Link } from 'react-router-dom';
import { Card } from './Card';
import { trackExternalLink } from '../utils/analytics';

export function Sections() {
  return (
    <>
      {/* What is Meshtastic Section */}
      <section id="about" className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 pointer-events-none" style={{ scrollMarginTop: '100px' }}>
        <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center pointer-events-auto">
          What is <span className="text-cyan-400">Meshtastic</span>?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Card title="📡 Off-Grid Messaging">
            Meshtastic enables long-range, encrypted messaging without internet, cell towers, 
            or subscription fees. Messages hop between devices using LoRa radio technology.
          </Card>
          <Card title="💰 Affordable Hardware">
            Devices cost $30-150 and run for weeks on a single battery charge. 
            Perfect for hiking, festivals, emergencies, or building community networks.
          </Card>
          <Card title="🔒 Private & Secure">
            End-to-end encrypted with AES-256. Your messages stay private. 
            No corporate servers, no data collection, no tracking.
          </Card>
          <Card title="🌍 Open Source">
            100% open source software and hardware designs. Contribute, customize, 
            and build upon it freely. Powered by a global community.
          </Card>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="relative z-10 bg-zinc-900/50 border-y border-zinc-800 pointer-events-none">
        <div className="mx-auto max-w-7xl px-6 py-8 pointer-events-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-cyan-400">10+ mi</div>
              <div className="text-sm text-zinc-400 mt-1">Line-of-sight range</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400">Days</div>
              <div className="text-sm text-zinc-400 mt-1">Battery life per charge</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400">$30+</div>
              <div className="text-sm text-zinc-400 mt-1">Device starting price</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400">100%</div>
              <div className="text-sm text-zinc-400 mt-1">Free & open source</div>
            </div>
          </div>
        </div>
      </section>

      {/* How Mesh Networks Work Section */}
      <section className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 pointer-events-none">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center pointer-events-auto">
          How <span className="text-cyan-400">Mesh Networks</span> Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          <Card title="🔗 Decentralized">
            Unlike traditional networks with central towers, mesh networks connect 
            peer-to-peer. Every device is both a user and a router, creating a 
            self-healing network with no single point of failure.
          </Card>
          <Card title="📶 Extended Range">
            Each device extends the network's reach. A message can hop through 
            multiple nodes to reach distant destinations—up to 7 hops by default. 
            One node's range might be 3 miles, but the mesh can span entire cities.
          </Card>
          <Card title="🔄 Automatic Routing">
            Messages find their own path through the network using flood routing. 
            If one route fails, the mesh automatically finds another. 
            No configuration needed—it just works.
          </Card>
        </div>
      </section>

      {/* Why Use Meshtastic Section */}
      <section className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 bg-zinc-900/30 rounded-2xl md:rounded-3xl pointer-events-none">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center pointer-events-auto">
          Why Choose <span className="text-cyan-400">Meshtastic</span>?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <Card title="🏕️ Outdoor Adventures">
            Stay connected while hiking, camping, or exploring remote areas. 
            Share your location, coordinate with your group, and call for help 
            when cell service doesn't exist.
          </Card>
          <Card title="🎪 Events & Festivals">
            Coordinate teams at festivals, races, or large gatherings without 
            relying on congested cell networks. Create private channels for 
            organizers and participants.
          </Card>
          <Card title="🚨 Emergency Preparedness">
            When disasters knock out traditional infrastructure, mesh networks 
            keep communities connected. No internet or power grid required—
            just battery-powered nodes.
          </Card>
          <Card title="🏘️ Community Building">
            Create neighborhood networks for sharing information, organizing 
            events, or just staying connected. Build resilient communication 
            infrastructure owned by the community.
          </Card>
          <Card title="🔬 Education & Experimentation">
            Learn about radio technology, networking, and distributed systems. 
            Perfect for students, hobbyists, and tinkerers exploring mesh 
            topology and RF propagation.
          </Card>
          <Card title="🌐 Privacy-First Communication">
            No corporate surveillance, no data harvesting, no algorithmic feeds. 
            Your conversations belong to you. Host your own infrastructure with 
            full control.
          </Card>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 pointer-events-none">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center pointer-events-auto">
          Get Started in <span className="text-cyan-400">3 Steps</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          <Card title="1️⃣ Get a Device">
            Choose from Heltec, LILYGO T-Beam, RAK WisBlock, or other compatible 
            hardware. Check out the{' '}
            <a
              href="https://meshtastic.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline pointer-events-auto"
              onClick={() => trackExternalLink('meshtastic.org', 'device_list')}
            >
              official device list
            </a>.
          </Card>
          <Card title="2️⃣ Flash Firmware">
            Install Meshtastic firmware using the web flasher—no technical 
            knowledge required. Configure your device with the mobile app 
            (iOS/Android) or web client.
          </Card>
          <Card title="3️⃣ Join a Network">
            Find and connect to local mesh communities in your area. 
            Place your node outdoors or at a window for best range. 
            Most communities are happy to help new members!
          </Card>
        </div>
      </section>

      {/* Interactive Mesh Simulation Section */}
      <section id="playground" className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 pb-16 md:pb-20 pointer-events-none">
        <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 pointer-events-auto">
          Interactive <span className="text-cyan-400">Mesh Simulator</span>
        </h2>
        <p className="text-sm md:text-base text-zinc-400 mb-6 md:mb-8 pointer-events-auto max-w-3xl">
          Explore how mesh networks work with our interactive visualization. Watch packets route through the network, 
          experiment with different topologies, and learn about Meshtastic's flood routing algorithm—all right in your browser.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <Card title="🎮 Interactive Playground">
            Click the "Playground" button in the header to enter full-screen mode. 
            Add nodes, drag them around, and watch connections form and break dynamically 
            based on distance—just like real LoRa radios.
          </Card>
          <Card title="📊 Real-Time Visualization">
            See packets traverse the network using Meshtastic-style flood routing. 
            Pause the simulation to inspect individual messages, their routes, hop counts, 
            and remaining hops before they expire.
          </Card>
          <Card title="🔧 Experiment with Settings">
            Toggle between KNN (simplified) and Radius (realistic) topology modes. 
            Adjust connection ranges to simulate different environments—from dense urban 
            areas to wide-open rural spaces. Control traffic, message types, and more.
          </Card>
        </div>
      </section>

      {/* Community Resources Link Section */}
      <section className="relative z-10 mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-12 pointer-events-none">
        <div className="bg-gradient-to-r from-cyan-400/5 to-cyan-600/5 border border-cyan-400/20 rounded-xl md:rounded-2xl p-6 md:p-8 text-center pointer-events-auto">
          <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">
            🌐 Explore Mesh <span className="text-cyan-400">Networks Worldwide</span>
          </h3>
          <p className="text-sm md:text-base text-zinc-400 mb-4 md:mb-6">
            Discover active Meshtastic communities, view live node maps, and connect with mesh enthusiasts around the globe.
          </p>
          <Link
            to="/community-resources"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-400 text-zinc-900 font-semibold rounded-full hover:bg-cyan-300 transition-all hover:scale-105"
          >
            View Community Resources
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Mesh Tools Link Section */}
      <section className="relative z-10 mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-12 pointer-events-none">
        <div className="bg-gradient-to-r from-cyan-600/5 to-cyan-400/5 border border-cyan-400/20 rounded-xl md:rounded-2xl p-6 md:p-8 text-center pointer-events-auto">
          <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">
            🛠️ Essential <span className="text-cyan-400">Mesh Tools</span>
          </h3>
          <p className="text-sm md:text-base text-zinc-400 mb-4 md:mb-6">
            Monitor, manage, and analyze your mesh network with powerful community-built tools and utilities.
          </p>
          <Link
            to="/mesh-tools"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-400 text-zinc-900 font-semibold rounded-full hover:bg-cyan-300 transition-all hover:scale-105"
          >
            Browse Mesh Tools
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>
    </>
  );
}
